/**
 * Binary wire format.
 *
 * JSON over WebSocket would work and would be easier to debug, but packet size
 * is the actual ceiling on how many players a session holds, and Durable
 * Objects bill inbound WebSocket messages at 20:1 — so the wire format is worth
 * getting right on day one rather than retrofitting.
 *
 * Full player state is 11 bytes here versus ~120 bytes of JSON.
 *
 * Everything is little-endian. Positions are quantized against the world
 * bounds in rules.ts, which makes those bounds part of the protocol: changing
 * them changes the meaning of every coordinate on the wire.
 */

import {
  WORLD_MAX_XZ,
  WORLD_MAX_Y,
  WORLD_MIN_XZ,
  WORLD_MIN_Y,
} from "./rules.js";

/**
 * Bumped whenever the wire format changes. The server rejects a mismatched
 * client at connect time rather than letting it silently desync — the modern
 * form of "encode everything two clients must agree on into session identity".
 */
export const PROTOCOL_VERSION = 5;

export const Msg = {
  // client -> server
  Hello: 1,
  Input: 2,
  Ping: 3,
  /**
   * The host asking to start the round.
   *
   * The only client message that is not simulation input or a clock probe, and
   * it carries nothing: who sent it is the socket's identity, and *when* is the
   * tick it arrives on. A payload would only be something to validate.
   *
   * It is a request, not a command. The server records it and the ordinary
   * `nextPhase` transition acts on it a tick later, so there is exactly one
   * code path into `Hiding` rather than a second one that skips the side
   * effects `transitionRound` owns. Sent by a client that is not the host, or
   * outside Warmup, it is ignored — the button is hidden for everyone else,
   * but that is a courtesy and this is the rule.
   */
  Start: 4,
  // server -> client
  Welcome: 128,
  Snapshot: 129,
  Pong: 130,
  PeerJoin: 131,
  PeerLeave: 132,
  Reject: 133,
  /**
   * Round phase and team assignments.
   *
   * Its own message rather than a snapshot header field, because the lessons
   * doc's distinction applies exactly: a snapshot is safe to drop since a newer
   * one supersedes it, but "the round just started and you are a hunter" is a
   * discrete event. Folding an event into a droppable packet is how you lose
   * it. Sent on every phase change and to each player as they join.
   */
  Round: 134,
  /**
   * Which prop instances are currently worn by somebody.
   *
   * Its own message for the same reason `Round` is one, and carrying the whole
   * set rather than a delta for a sharper version of it: every client
   * suppresses the collision solid of every possessed prop, and a client that
   * missed one delta would simulate a different world from the server for the
   * rest of the round. A positional disagreement converges; a collision-world
   * disagreement does not. Whole-set means a single message repairs any amount
   * of prior loss.
   *
   * It carries prop indices and no player ids at all. Which props are worn is
   * something anyone can see by looking at where props are not; *who* is
   * wearing one is the thing a hunter must never receive.
   */
  Possess: 135,
  /**
   * A shot was fired, and what it did.
   *
   * An *event*, in the same sense `Round` is one and a snapshot is not — and
   * deliberately not the authority on anything. Whether a player has been found
   * is state, and lives in `Round`'s per-player flags where a client that
   * missed this message is repaired by the next whole-set broadcast. This
   * carries only what an event needs to carry: enough to play a reveal, a
   * sound, a tracer.
   *
   * Broadcast to everyone rather than sent to the shooter. A miss is a
   * gunshot — public by nature, and hearing one is information hiders are meant
   * to have. A hit is a player being revealed, which is public by definition.
   * There is nothing here to protect: the target has already stopped being
   * hidden by the time this is sent.
   */
  Tag: 136,
  /**
   * A hider made a noise, and where.
   *
   * An event like `Msg.Tag`, and droppable for a stronger reason than that one:
   * a taunt that goes missing is a taunt nobody heard, which is a sound card
   * problem rather than a state problem. Nothing derives from it.
   *
   * It is also the only message in this protocol that publishes a hider's
   * position, which is the thing `entityViews` exists to withhold — so two
   * things constrain it. It carries **no player id**: a hunter learns that
   * something over there is a person, never which person, and never which of
   * the crates in front of them it is. And it is **culled by distance** on the
   * server (see `tauntAudible`), so a client that reads its own socket learns
   * only what an honest pair of ears in the same place would have.
   */
  Taunt: 137,
} as const;

export const RejectReason = {
  ProtocolMismatch: 1,
  LobbyFull: 2,
  BadName: 3,
  /**
   * Client and server built collision from different map data. Refused here
   * rather than let it through: a client whose walls are in different places
   * than the server's doesn't error, it just quietly disagrees with every
   * collision result forever. See mapfmt.ts's mapHash.
   */
  MapMismatch: 4,
} as const;

/**
 * Close codes in the application-private 4000–4999 range.
 *
 * Where a close code corresponds to a `RejectReason`, it is `4000 + reason`, so
 * a code in a browser console names the same condition as the Reject frame that
 * preceded it without a second table to keep in sync. `4003` is the exception:
 * it is a connection timeout, which is not a rejected join at all. (It shares a
 * number with `RejectReason.BadName`, which the server has never emitted —
 * names are sanitized rather than refused. If a join ever *is* refused for a
 * name, it needs a code outside the arithmetic rather than the collision.)
 *
 * Shared rather than server-private because the client now reads these: the
 * range as a whole is the server having made a decision about this client, and
 * reconnecting repeats it. See `isRetryableClose` in the client.
 */
export const CloseCode = {
  ProtocolMismatch: 4001,
  LobbyFull: 4002,
  TimedOut: 4003,
  MapMismatch: 4004,
} as const;

/** Per-entity state flags, packed into one byte of each snapshot entry. */
export const EntFlag = {
  OnGround: 1 << 0,
  Crouching: 1 << 1,
  Moving: 1 << 2,
} as const;

/**
 * Per-player flags in `Msg.Round`'s roster.
 *
 * A flags byte rather than widening `team`, because being found is not a team.
 * `assignTeams` reads the previous team to guarantee nobody hunts twice in a
 * row (`teamAfterSwap`), so overwriting a found hider's team with a spectator
 * value would quietly drop them out of that guarantee — and the bug would
 * surface a whole round later as "why am I hunting again".
 */
export const PlayerFlag = {
  /** Found by a hunter. Spectating until the round ends. */
  Found: 1 << 0,
} as const;

// ---------------------------------------------------------------------------
// Quantization
// ---------------------------------------------------------------------------

const U16_STEPS = 65535;

export function quantize(value: number, min: number, max: number, steps: number): number {
  const t = (value - min) / (max - min);
  const q = Math.round(t * steps);
  return q < 0 ? 0 : q > steps ? steps : q;
}

export function dequantize(q: number, min: number, max: number, steps: number): number {
  return min + (q / steps) * (max - min);
}

/** ~1.95 mm resolution over a 128 m span. */
export const quantizeXZ = (v: number): number => quantize(v, WORLD_MIN_XZ, WORLD_MAX_XZ, U16_STEPS);
export const dequantizeXZ = (q: number): number => dequantize(q, WORLD_MIN_XZ, WORLD_MAX_XZ, U16_STEPS);

/** ~0.98 mm resolution over a 64 m span. */
export const quantizeY = (v: number): number => quantize(v, WORLD_MIN_Y, WORLD_MAX_Y, U16_STEPS);
export const dequantizeY = (q: number): number => dequantize(q, WORLD_MIN_Y, WORLD_MAX_Y, U16_STEPS);

/**
 * Yaw over [-PI, PI], ~0.0055 degrees per step.
 *
 * Note that +PI and -PI quantize to different integers despite being the same
 * heading. Harmless — they dequantize back to angles that differ by 2*PI, and
 * every consumer runs them through wrapAngle/lerpAngle.
 */
export const quantizeYaw = (v: number): number => quantize(v, -Math.PI, Math.PI, U16_STEPS);
export const dequantizeYaw = (q: number): number => dequantize(q, -Math.PI, Math.PI, U16_STEPS);

const HALF_PI = Math.PI / 2;
export const quantizePitch = (v: number): number => quantize(v, -HALF_PI, HALF_PI, U16_STEPS);
export const dequantizePitch = (q: number): number => dequantize(q, -HALF_PI, HALF_PI, U16_STEPS);

/**
 * Round a value through the wire quantizer without encoding it.
 *
 * The client MUST apply this to its own yaw/pitch before feeding an input
 * frame to the predictor, so that it predicts using exactly the value the
 * server will decode. Skipping this puts a small guaranteed mismatch into
 * every single tick.
 */
export const roundTripYaw = (v: number): number => dequantizeYaw(quantizeYaw(v));
export const roundTripPitch = (v: number): number => dequantizePitch(quantizePitch(v));

// ---------------------------------------------------------------------------
// Byte IO
// ---------------------------------------------------------------------------

const textEncoder = new TextEncoder();
const textDecoder = new TextDecoder();

export class ByteWriter {
  /**
   * Held separately from `bytes.buffer`, which types as `ArrayBufferLike` —
   * i.e. possibly shared — and so cannot be handed to `WebSocket.send`. We
   * allocated it here and know it is not shared; keeping the reference says so
   * without a cast.
   */
  private readonly buffer: ArrayBuffer;
  private readonly bytes: Uint8Array;
  private readonly view: DataView;
  offset = 0;

  constructor(capacity = 2048) {
    this.buffer = new ArrayBuffer(capacity);
    this.bytes = new Uint8Array(this.buffer);
    this.view = new DataView(this.buffer);
  }

  reset(): void {
    this.offset = 0;
  }

  u8(v: number): void {
    this.view.setUint8(this.offset, v);
    this.offset += 1;
  }

  u16(v: number): void {
    this.view.setUint16(this.offset, v, true);
    this.offset += 2;
  }

  u32(v: number): void {
    this.view.setUint32(this.offset, v, true);
    this.offset += 4;
  }

  f32(v: number): void {
    this.view.setFloat32(this.offset, v, true);
    this.offset += 4;
  }

  f64(v: number): void {
    this.view.setFloat64(this.offset, v, true);
    this.offset += 8;
  }

  /** Length-prefixed UTF-8, max 255 bytes. */
  str(s: string): void {
    const encoded = textEncoder.encode(s);
    const len = Math.min(encoded.length, 255);
    this.u8(len);
    this.bytes.set(encoded.subarray(0, len), this.offset);
    this.offset += len;
  }

  /** A copy of the written region, ready to hand to `WebSocket.send`. */
  finish(): ArrayBuffer {
    return this.buffer.slice(0, this.offset);
  }
}

export class ByteReader {
  private readonly bytes: Uint8Array;
  private readonly view: DataView;
  offset = 0;

  constructor(data: ArrayBuffer | Uint8Array) {
    if (data instanceof Uint8Array) {
      this.bytes = data;
      this.view = new DataView(data.buffer, data.byteOffset, data.byteLength);
    } else {
      this.bytes = new Uint8Array(data);
      this.view = new DataView(data);
    }
  }

  get remaining(): number {
    return this.bytes.byteLength - this.offset;
  }

  u8(): number {
    const v = this.view.getUint8(this.offset);
    this.offset += 1;
    return v;
  }

  u16(): number {
    const v = this.view.getUint16(this.offset, true);
    this.offset += 2;
    return v;
  }

  u32(): number {
    const v = this.view.getUint32(this.offset, true);
    this.offset += 4;
    return v;
  }

  f32(): number {
    const v = this.view.getFloat32(this.offset, true);
    this.offset += 4;
    return v;
  }

  f64(): number {
    const v = this.view.getFloat64(this.offset, true);
    this.offset += 8;
    return v;
  }

  str(): string {
    const len = this.u8();
    // A length prefix longer than the buffer is a malformed message. Without
    // this check `subarray` silently clamps and we accept a truncated string
    // as if it were fine — safe by accident rather than by decision. Throwing
    // lets the decoder reject it the same way it rejects any other garbage.
    if (len > this.remaining) {
      throw new RangeError("string length prefix overruns buffer");
    }
    const s = textDecoder.decode(this.bytes.subarray(this.offset, this.offset + len));
    this.offset += len;
    return s;
  }
}

// ---------------------------------------------------------------------------
// Messages
// ---------------------------------------------------------------------------

/**
 * Note what is *not* here: a map hash.
 *
 * Hello is the first thing on the socket, and a lobby is on a level chosen when
 * it was created — so a joiner does not yet know which map it is about to be
 * on, and any hash it offered would be a guess about a level it has not been
 * told. Map agreement therefore moved to `Welcome`, which names the level and
 * hashes it; see `WelcomeMsg.mapId`.
 */
export interface HelloMsg {
  type: typeof Msg.Hello;
  protocolVersion: number;
  name: string;
}

export interface WireInputFrame {
  seq: number;
  buttons: number;
  /** Quantized integers, exactly as they travel. */
  yawQ: number;
  pitchQ: number;
  /**
   * The server tick the client believes it predicted this frame at.
   *
   * A claim, not a fact, and the server treats it as one — it is clamped into
   * a window around the server's own tick before anything reads it. Inside
   * that window it is exactly the information the server was missing, and it
   * answers two questions that both used to be answered wrongly:
   *
   *  - **Which phase gates this frame.** The server applies up to
   *    MAX_INPUTS_PER_TICK frames in one tick and used to gate them all on the
   *    phase at the tick it *dequeued* them, while the client predicted each
   *    one at a tick of its own. A queue backing up across the hunter release
   *    made the two strip different bits from the same frame — the residue of
   *    the phase-transition desync, which `phaseAtTick` bounded to a couple of
   *    ticks per round but could not remove. Gating on the frame's own tick
   *    removes it.
   *
   *  - **How far to rewind to resolve a shot.** The client aims at entities
   *    rendered INTERP_DELAY_MS behind its own tick, so this minus that delay
   *    is the world it was actually looking at. Sending the frame tick rather
   *    than an acknowledged snapshot tick means one field answers both, and
   *    the two answers cannot drift apart.
   *
   * Positional, like `seq`: only the newest frame's value travels and the rest
   * are implied by their offset from it, so redundancy stays free.
   */
  tick: number;
}

export interface InputMsg {
  type: typeof Msg.Input;
  /** Frames oldest-first; the last frame's seq and tick are the newest. */
  frames: WireInputFrame[];
}

export interface PingMsg {
  type: typeof Msg.Ping;
  clientTime: number;
}

/** See `Msg.Start`. Carries nothing; the socket says who, the tick says when. */
export interface StartMsg {
  type: typeof Msg.Start;
}

/**
 * `serverTimeMs` throughout this protocol is the server's *simulation*
 * timeline (tick * TICK_MS), not a wall clock.
 *
 * Two reasons. Cloudflare's runtime deliberately does not advance `Date.now()`
 * freely between I/O operations, so a wall clock read inside a tick loop is not
 * something to build a timeline on. And the thing the client actually needs to
 * interpolate against is the simulation's notion of time, not the server
 * machine's. It is sent as a float rather than a 32-bit millisecond counter
 * specifically so there is no wraparound case to get wrong once every 49 days.
 */
export interface WelcomeMsg {
  type: typeof Msg.Welcome;
  yourId: number;
  tickRate: number;
  serverTimeMs: number;
  /**
   * Which level this lobby is on — the registry id, `MAPS` in maps/index.ts.
   *
   * This is the client's first and only word on the subject: it is fixed when
   * the lobby is created, so it arrives once and never changes for the life of
   * the session. The client looks the id up, builds its collision world and its
   * renderer from what it finds, and only then starts simulating.
   */
  mapId: string;
  /**
   * The server's `mapHash` (see mapfmt.ts) for `mapId`, as a raw u32 — never
   * the hex string, which exists only for logging and display.
   *
   * The client hashes its own copy of `mapId` and compares. Equal means both
   * sides built collision from the same geometry; different, or an id this
   * client has never heard of, means the client build is stale and it closes
   * rather than simulating against a level it only thinks it has. The check
   * lives on the client because the client is the side that is wrong, and it
   * can say so in terms of a named level rather than two unequal integers.
   */
  mapHash: number;
  peers: { id: number; name: string }[];
}

export interface SnapshotEntity {
  id: number;
  propId: number;
  xQ: number;
  yQ: number;
  zQ: number;
  yawQ: number;
  flags: number;
}

/**
 * The recipient's own authoritative state, at full precision.
 *
 * Reconciliation replays unacknowledged inputs forward from the server's
 * state, which means it needs the server's *velocity* — replaying from a
 * position-only snapshot restarts every replay from a standstill and diverges
 * immediately.
 *
 * This is per-recipient rather than per-entity: only you need your own
 * velocity, because everyone else's entity is interpolated between snapshots
 * rather than simulated. 29 bytes on an outbound message, and Cloudflare does
 * not bill outbound WebSocket data at all.
 *
 * f32 rather than f64: ~4 micrometre resolution over the world bounds, which
 * is four orders of magnitude below RECONCILE_EPSILON.
 */
export interface SelfState {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  onGround: boolean;
}

export interface SnapshotMsg {
  type: typeof Msg.Snapshot;
  serverTimeMs: number;
  tick: number;
  /** Newest input seq from this client that the server has consumed. */
  lastProcessedSeq: number;
  self: SelfState;
  /**
   * The prop instance this recipient is wearing, or `NO_PROP`.
   *
   * Per-recipient like `self`, and for the same reason: it is the one piece of
   * possession state the client needs in order to *simulate*, because it fixes
   * which hull `step` runs with. Everyone else's disguise is a rendering
   * question answered by `SnapshotEntity.propId`.
   *
   * In the snapshot rather than only in `Msg.Possess` because it is state, not
   * an event — a client that reconnects or drops a packet must be able to
   * recover its own hull from the next snapshot alone, and a hull that is
   * wrong until the next possession event would desync the moment it moved.
   */
  selfPropIndex: number;
  entities: SnapshotEntity[];
}

/** See `Msg.Possess`. Prop indices only — never who is wearing them. */
export interface PossessMsg {
  type: typeof Msg.Possess;
  propIndices: number[];
}

export interface PongMsg {
  type: typeof Msg.Pong;
  clientTime: number;
  serverTimeMs: number;
}

export interface PeerJoinMsg {
  type: typeof Msg.PeerJoin;
  id: number;
  name: string;
}

export interface PeerLeaveMsg {
  type: typeof Msg.PeerLeave;
  id: number;
}

export interface RejectMsg {
  type: typeof Msg.Reject;
  reason: number;
  detail: string;
}

/**
 * The round, as broadcast on every phase change.
 *
 * `phaseStartedTick` rather than a remaining-time field: the client already
 * tracks the server tick, so a start tick lets it compute the countdown itself
 * every frame instead of watching a number decay between packets. It also means
 * a dropped or late Round message costs nothing — the deadline is still
 * derivable from the next snapshot's tick.
 *
 * That second property is what `round.phaseAtTick` turns into the movement
 * gate: the client derives the phase for the tick it is predicting rather than
 * using the phase this message last carried, so a hunter is released from the
 * Hiding freeze on the same tick on both sides instead of ~RTT/2 apart.
 *
 * Team assignments are carried whole rather than as a delta. There are at most
 * MAX_PLAYERS of them at three bytes each, this is sent a handful of times per
 * round, and a delta that goes missing leaves a client permanently wrong about
 * who is hunting.
 *
 * The roster carries `flags` (see `PlayerFlag`) alongside the team, which is
 * what makes *found* a state rather than an event. `Msg.Tag` announces a shot;
 * this settles who is still playing. A find therefore triggers a broadcast of
 * this message, so the answer is always one whole-set message away and a
 * dropped event cannot leave a client permanently wrong about who is left —
 * the same argument `Msg.Possess` makes for carrying the whole possessed set.
 */
export interface RoundMsg {
  type: typeof Msg.Round;
  phase: number;
  phaseStartedTick: number;
  roundNumber: number;
  huntersWon: number;
  hidersWon: number;
  /**
   * The player whose START begins the round, or 0 for an empty lobby.
   *
   * Here rather than in its own message because it is *state*, exactly as the
   * roster is: it changes when somebody leaves, not on a schedule, and a client
   * that missed the change must not be left offering a button that does
   * nothing — or hiding one that would work. This message is already the
   * whole-set answer to "who is in this lobby and what are they", already
   * broadcast on every phase change, and already sent to a joiner on its own;
   * a host change now broadcasts it too.
   */
  hostId: number;
  /**
   * Tick the lobby will start itself on, or 0 for no pending auto-start.
   *
   * Display only — the client shows the countdown and gates nothing on it. It
   * cannot: the server may leave Warmup earlier than this, the moment the host
   * presses START.
   *
   * u32 like `phaseStartedTick` and for the same reason: it is subtracted from
   * the client's own unwrapped tick estimate, so a wrap here would be a wrong
   * countdown rather than a cosmetic one.
   */
  startsAtTick: number;
  /** Every connected player's team and flags, including the recipient's own. */
  teams: { id: number; team: number; flags: number }[];
}

/**
 * A shot, and what it did. See `Msg.Tag`.
 *
 * `propIndex` is the disguise the target was wearing, so a client can play the
 * reveal on the right prop instance rather than on a player it was never
 * rendering as one. `NO_PROP` when the target was undisguised, and on a miss.
 */
export interface TagMsg {
  type: typeof Msg.Tag;
  shooterId: number;
  /** A `TagOutcome`. */
  outcome: number;
  /** The player found, or `NO_TARGET` (0). */
  targetId: number;
  propIndex: number;
}

/**
 * A taunt, and where it came from. See `Msg.Taunt`.
 *
 * Positions are quantized exactly as a snapshot's are, so a taunt lands on the
 * same grid as everything else on the wire and needs no second codec.
 *
 * `self` is the one per-recipient field in the message, which is why a taunt is
 * encoded twice — once for the room, once for the player it came out of. A
 * taunt is involuntary, so the person it happened to has to be told it did:
 * they hear it centred on themselves, where it is indistinguishable from
 * somebody standing next to them, and a player with no speakers would otherwise
 * have their position published with no signal at all. It can only ever say
 * *you*, never *them* — the message still carries no id for anyone else.
 */
export interface TauntMsg {
  type: typeof Msg.Taunt;
  /** A `TauntSoundId`. */
  sound: number;
  xQ: number;
  yQ: number;
  zQ: number;
  /** True only in the copy sent to the hider who taunted. */
  self: boolean;
}

export type ClientMessage = HelloMsg | InputMsg | PingMsg | StartMsg;
export type ServerMessage =
  | WelcomeMsg
  | SnapshotMsg
  | PongMsg
  | PeerJoinMsg
  | PeerLeaveMsg
  | RejectMsg
  | RoundMsg
  | PossessMsg
  | TagMsg
  | TauntMsg;

// --- encoders --------------------------------------------------------------

export function encodeHello(w: ByteWriter, name: string): ArrayBuffer {
  w.reset();
  w.u8(Msg.Hello);
  w.u16(PROTOCOL_VERSION);
  w.str(name);
  return w.finish();
}

export function encodeInput(w: ByteWriter, frames: readonly WireInputFrame[]): ArrayBuffer {
  w.reset();
  w.u8(Msg.Input);
  const count = Math.min(frames.length, 255);
  const newest = frames[frames.length - 1]!;
  w.u16(newest.seq & 0xffff);
  w.u16(newest.tick & 0xffff);
  w.u8(count);
  // Oldest first. Sequence numbers are implied by position relative to the
  // newest, so they cost nothing per frame.
  for (let i = frames.length - count; i < frames.length; i++) {
    const f = frames[i]!;
    w.u8(f.buttons);
    w.u16(f.yawQ);
    w.u16(f.pitchQ);
  }
  return w.finish();
}

export function encodePing(w: ByteWriter, clientTime: number): ArrayBuffer {
  w.reset();
  w.u8(Msg.Ping);
  w.f64(clientTime);
  return w.finish();
}

/** One byte. See `Msg.Start`. */
export function encodeStart(w: ByteWriter): ArrayBuffer {
  w.reset();
  w.u8(Msg.Start);
  return w.finish();
}

export function encodeWelcome(
  w: ByteWriter,
  yourId: number,
  tickRate: number,
  serverTimeMs: number,
  mapId: string,
  mapHash: number,
  peers: readonly { id: number; name: string }[],
): ArrayBuffer {
  w.reset();
  w.u8(Msg.Welcome);
  w.u8(yourId);
  w.u8(tickRate);
  w.f64(serverTimeMs);
  w.str(mapId);
  w.u32(mapHash);
  w.u8(peers.length);
  for (const p of peers) {
    w.u8(p.id);
    w.str(p.name);
  }
  return w.finish();
}

export function encodeSnapshot(
  w: ByteWriter,
  serverTimeMs: number,
  tick: number,
  lastProcessedSeq: number,
  self: SelfState,
  selfPropIndex: number,
  entities: readonly SnapshotEntity[],
): ArrayBuffer {
  w.reset();
  w.u8(Msg.Snapshot);
  w.f64(serverTimeMs);
  w.u16(tick & 0xffff);
  w.u16(lastProcessedSeq & 0xffff);
  w.f32(self.x);
  w.f32(self.y);
  w.f32(self.z);
  w.f32(self.vx);
  w.f32(self.vy);
  w.f32(self.vz);
  w.u8(self.onGround ? 1 : 0);
  w.u16(selfPropIndex);
  w.u8(entities.length);
  for (const e of entities) {
    w.u8(e.id);
    w.u8(e.propId);
    w.u16(e.xQ);
    w.u16(e.yQ);
    w.u16(e.zQ);
    w.u16(e.yawQ);
    w.u8(e.flags);
  }
  return w.finish();
}

export function encodePossess(w: ByteWriter, propIndices: readonly number[]): ArrayBuffer {
  w.reset();
  w.u8(Msg.Possess);
  w.u8(propIndices.length);
  for (const i of propIndices) w.u16(i);
  return w.finish();
}

export function encodePong(w: ByteWriter, clientTime: number, serverTimeMs: number): ArrayBuffer {
  w.reset();
  w.u8(Msg.Pong);
  w.f64(clientTime);
  w.f64(serverTimeMs);
  return w.finish();
}

export function encodePeerJoin(w: ByteWriter, id: number, name: string): ArrayBuffer {
  w.reset();
  w.u8(Msg.PeerJoin);
  w.u8(id);
  w.str(name);
  return w.finish();
}

export function encodePeerLeave(w: ByteWriter, id: number): ArrayBuffer {
  w.reset();
  w.u8(Msg.PeerLeave);
  w.u8(id);
  return w.finish();
}

export function encodeReject(w: ByteWriter, reason: number, detail: string): ArrayBuffer {
  w.reset();
  w.u8(Msg.Reject);
  w.u8(reason);
  w.str(detail);
  return w.finish();
}

export function encodeRound(
  w: ByteWriter,
  phase: number,
  phaseStartedTick: number,
  roundNumber: number,
  huntersWon: number,
  hidersWon: number,
  hostId: number,
  startsAtTick: number,
  teams: readonly { id: number; team: number; flags: number }[],
): ArrayBuffer {
  w.reset();
  w.u8(Msg.Round);
  w.u8(phase);
  // Full width, unlike the snapshot's u16 tick. This one is subtracted from the
  // client's own unwrapped tick estimate to derive the current phase, so a wrap
  // is not a display artefact — it is every derived phase being wrong for the
  // rest of the session, starting 65536 ticks (~54.6 minutes at 20Hz) in. The
  // snapshot's tick stays u16 because it is only ordered with `seqNewer` and
  // rendered, and neither use cares about the absolute value.
  w.u32(phaseStartedTick);
  w.u16(roundNumber);
  w.u8(huntersWon);
  w.u8(hidersWon);
  w.u8(hostId);
  // Full width, like phaseStartedTick above and for the same reason.
  w.u32(startsAtTick);
  w.u8(teams.length);
  for (const t of teams) {
    w.u8(t.id);
    w.u8(t.team);
    w.u8(t.flags);
  }
  return w.finish();
}

export function encodeTag(
  w: ByteWriter,
  shooterId: number,
  outcome: number,
  targetId: number,
  propIndex: number,
): ArrayBuffer {
  w.reset();
  w.u8(Msg.Tag);
  w.u8(shooterId);
  w.u8(outcome);
  w.u8(targetId);
  w.u16(propIndex);
  return w.finish();
}

/** 9 bytes. See `TauntMsg` for why `self` is on the wire at all. */
export function encodeTaunt(
  w: ByteWriter,
  sound: number,
  xQ: number,
  yQ: number,
  zQ: number,
  self: boolean,
): ArrayBuffer {
  w.reset();
  w.u8(Msg.Taunt);
  w.u8(sound);
  w.u16(xQ);
  w.u16(yQ);
  w.u16(zQ);
  w.u8(self ? 1 : 0);
  return w.finish();
}

// --- decoders --------------------------------------------------------------

/** Returns null for anything unrecognised or malformed. Never throws. */
export function decodeClientMessage(data: ArrayBuffer | Uint8Array): ClientMessage | null {
  try {
    const r = new ByteReader(data);
    const type = r.u8();
    switch (type) {
      case Msg.Hello: {
        const protocolVersion = r.u16();
        const name = r.str();
        return { type: Msg.Hello, protocolVersion, name };
      }
      case Msg.Input: {
        const newestSeq = r.u16();
        const newestTick = r.u16();
        const count = r.u8();
        const frames: WireInputFrame[] = [];
        for (let i = 0; i < count; i++) {
          const buttons = r.u8();
          const yawQ = r.u16();
          const pitchQ = r.u16();
          // Sequence numbers and ticks are both positional and both wrap at
          // 2^16. They step together because the client emits exactly one
          // frame per simulation tick — see `runFixedSteps`.
          const back = count - 1 - i;
          frames.push({
            seq: (newestSeq - back) & 0xffff,
            buttons,
            yawQ,
            pitchQ,
            tick: (newestTick - back) & 0xffff,
          });
        }
        return { type: Msg.Input, frames };
      }
      case Msg.Ping:
        return { type: Msg.Ping, clientTime: r.f64() };
      case Msg.Start:
        return { type: Msg.Start };
      default:
        return null;
    }
  } catch {
    return null;
  }
}

/** Returns null for anything unrecognised or malformed. Never throws. */
export function decodeServerMessage(data: ArrayBuffer | Uint8Array): ServerMessage | null {
  try {
    const r = new ByteReader(data);
    const type = r.u8();
    switch (type) {
      case Msg.Welcome: {
        const yourId = r.u8();
        const tickRate = r.u8();
        const serverTimeMs = r.f64();
        const mapId = r.str();
        const mapHash = r.u32();
        const peerCount = r.u8();
        const peers: { id: number; name: string }[] = [];
        for (let i = 0; i < peerCount; i++) {
          peers.push({ id: r.u8(), name: r.str() });
        }
        return { type: Msg.Welcome, yourId, tickRate, serverTimeMs, mapId, mapHash, peers };
      }
      case Msg.Snapshot: {
        const serverTimeMs = r.f64();
        const tick = r.u16();
        const lastProcessedSeq = r.u16();
        const self: SelfState = {
          x: r.f32(),
          y: r.f32(),
          z: r.f32(),
          vx: r.f32(),
          vy: r.f32(),
          vz: r.f32(),
          onGround: r.u8() !== 0,
        };
        const selfPropIndex = r.u16();
        const count = r.u8();
        const entities: SnapshotEntity[] = [];
        for (let i = 0; i < count; i++) {
          entities.push({
            id: r.u8(),
            propId: r.u8(),
            xQ: r.u16(),
            yQ: r.u16(),
            zQ: r.u16(),
            yawQ: r.u16(),
            flags: r.u8(),
          });
        }
        return {
          type: Msg.Snapshot,
          serverTimeMs,
          tick,
          lastProcessedSeq,
          self,
          selfPropIndex,
          entities,
        };
      }
      case Msg.Possess: {
        const count = r.u8();
        const propIndices: number[] = [];
        for (let i = 0; i < count; i++) propIndices.push(r.u16());
        return { type: Msg.Possess, propIndices };
      }
      case Msg.Tag:
        return {
          type: Msg.Tag,
          shooterId: r.u8(),
          outcome: r.u8(),
          targetId: r.u8(),
          propIndex: r.u16(),
        };
      case Msg.Taunt:
        return {
          type: Msg.Taunt,
          sound: r.u8(),
          xQ: r.u16(),
          yQ: r.u16(),
          zQ: r.u16(),
          self: r.u8() !== 0,
        };
      case Msg.Pong:
        return { type: Msg.Pong, clientTime: r.f64(), serverTimeMs: r.f64() };
      case Msg.PeerJoin:
        return { type: Msg.PeerJoin, id: r.u8(), name: r.str() };
      case Msg.PeerLeave:
        return { type: Msg.PeerLeave, id: r.u8() };
      case Msg.Reject:
        return { type: Msg.Reject, reason: r.u8(), detail: r.str() };
      case Msg.Round: {
        const phase = r.u8();
        const phaseStartedTick = r.u32();
        const roundNumber = r.u16();
        const huntersWon = r.u8();
        const hidersWon = r.u8();
        const hostId = r.u8();
        const startsAtTick = r.u32();
        const count = r.u8();
        const teams: { id: number; team: number; flags: number }[] = [];
        for (let i = 0; i < count; i++) {
          teams.push({ id: r.u8(), team: r.u8(), flags: r.u8() });
        }
        return {
          type: Msg.Round,
          phase,
          phaseStartedTick,
          roundNumber,
          huntersWon,
          hidersWon,
          hostId,
          startsAtTick,
          teams,
        };
      }
      default:
        return null;
    }
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Sequence arithmetic
// ---------------------------------------------------------------------------

/**
 * Is `a` newer than `b`, accounting for 16-bit wraparound?
 *
 * Lessons-learned: use sequence numbers, don't assume delivery semantics.
 * WebSocket is reliable and ordered, so stale packets should not occur — but
 * "should not occur" is exactly the assumption that produces the bug where a
 * redelivered packet silently rewinds a player's position.
 */
export function seqNewer(a: number, b: number): boolean {
  return ((a - b) & 0xffff) !== 0 && ((a - b) & 0xffff) < 0x8000;
}

/** Signed distance from `b` to `a`, accounting for wraparound. */
export function seqDelta(a: number, b: number): number {
  const d = (a - b) & 0xffff;
  return d < 0x8000 ? d : d - 0x10000;
}

/**
 * Recover a full tick from the low 16 bits that travelled, given a full tick
 * near it.
 *
 * The wire carries 16 bits and the server counts without wrapping, so every
 * received tick has to be re-attached to the server's own counter. `seqDelta`
 * already does the wraparound arithmetic; this only names what it means when
 * the numbers are ticks rather than sequence numbers.
 */
export function unwrapTick(wireTick: number, referenceTick: number): number {
  return referenceTick + seqDelta(wireTick, referenceTick & 0xffff);
}

/**
 * The tick an input frame is treated as belonging to: what the client claimed,
 * clamped into a window the server is willing to believe.
 *
 * `WireInputFrame.tick` is a claim, and both things that read it — the movement
 * gate and the shot rewind — become exploits if it is taken at face value.
 *
 * The upper clamp is the load-bearing one. A client naming a *future* tick
 * would gate itself on a phase the server has not reached, which is a hunter
 * releasing themselves from the Hiding freeze early. Pinning to the server's
 * own tick makes that impossible rather than merely unlikely.
 *
 * The lower clamp bounds the opposite trick. Naming an old tick is usually
 * self-punishing — an earlier phase is more restrictive, not less — with one
 * exception: during `Over`, where everyone is frozen, a stale tick still inside
 * `Hunting` would keep a player moving through the scoreboard. `maxLag` bounds
 * that to a second of walking rather than the whole eight, and an honest client
 * whose clock estimate is within a second of the truth never reaches the clamp
 * at all.
 */
export function resolveFrameTick(
  wireTick: number,
  serverTick: number,
  maxLag: number,
): number {
  const claimed = unwrapTick(wireTick, serverTick);
  if (claimed > serverTick) return serverTick;
  if (claimed < serverTick - maxLag) return serverTick - maxLag;
  return claimed;
}
